// built-from: /engagement-discovery-plan v1 impl:engagement-discovery-plan role:implementation
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEngagement } from './useEngagement';

export type DiscoveryDecisionOutcome = 'accept' | 'reject';

export interface DiscoveryScopeDecision {
  id: string;
  state: 'proposed' | 'accepted' | 'rejected';
  definitionId: string;
  [key: string]: unknown;
}

export interface DurableDiscoveryActivity {
  id: string;
  sourceDecisionId?: string;
  definition: {
    id: string;
    name: string;
    method: string;
    purpose: string;
  };
  owner?: { name?: string } | string;
  participants?: unknown[];
  expectedOutput?: { name?: string } | string;
  reviewer?: { id: string; name: string; role?: string };
  rationale?: string;
  question?: string;
  streamId?: string;
  boundaries?: { start: string; end: string };
  workspace?: string;
  state: 'proposed' | 'planned' | 'in-progress' | 'awaiting-review' | 'complete';
  sessionIds?: string[];
  outputs?: DurableActivityOutput[];
}

export interface VsmCurrentStateSnapshotPayload {
  flowText?: string;
  summary?: string;
  assumptions?: string[];
  openQuestions?: string[];
  currentState?: {
    stream?: unknown;
    session?: unknown;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface ActivityOutputSnapshot {
  type: 'vsm.current-state';
  schemaVersion: 1;
  payload: VsmCurrentStateSnapshotPayload;
}

export interface ActivityOutputVersion {
  version: number;
  state: 'ready-for-review' | 'changes-requested' | 'reviewed';
  returnId: string;
  returnedAt: string;
  sessionId: string;
  snapshot: ActivityOutputSnapshot;
  reviewedAt?: string;
  requestedAt?: string;
}

export interface DurableActivityOutput {
  id: string;
  name: string;
  method: string;
  reviewer: { id: string; name: string; role?: string };
  contributors: Array<{ id: string; name: string; role?: string }>;
  versions: ActivityOutputVersion[];
}

export interface ReturnActivityOutputInput {
  sessionId: string;
  returnId: string;
  returnedAt: string;
  output?: Omit<DurableActivityOutput, 'versions'> & { snapshot: ActivityOutputSnapshot };
}

export interface CurrentStateMappingSetupInput {
  question: string;
  streamName: string;
  startBoundary: string;
  endBoundary: string;
  participants: string;
  reviewer: string;
}

export interface CurrentStateMappingStartResult {
  status: 'ready';
  streamId: string;
  activityId: string;
  sessionId: string;
  revision: string | null;
}

export type CurrentStateMappingStartStatus = 'idle' | 'starting' | 'resuming' | 'recovery' | 'failed';

export interface ActivityOutputCommandError {
  command: 'link' | 'return' | 'changes' | 'review';
  message: string;
  conflict?: boolean;
}

export interface DiscoveryPlan {
  version: number;
  scopeDecisions: DiscoveryScopeDecision[];
  activities: DurableDiscoveryActivity[];
}

export interface DiscoveryPlanError {
  decisionId: string | null;
  message: string;
  conflict?: boolean;
}

export interface UseDiscoveryPlanReturn {
  plan: DiscoveryPlan | null;
  revision: string | null;
  loading: boolean;
  pendingDecisionId: string | null;
  error: DiscoveryPlanError | null;
  resolveDecision: (decisionId: string, outcome: DiscoveryDecisionOutcome) => Promise<void>;
  retryDecision: (decisionId: string) => Promise<void>;
  loadLatestPlan: () => Promise<void>;
  pendingActivityCommand: ActivityOutputCommandError['command'] | null;
  activityOutputError: ActivityOutputCommandError | null;
  linkActivitySession: (activityId: string, sessionId: string) => Promise<boolean>;
  returnActivityOutput: (activityId: string, input: ReturnActivityOutputInput) => Promise<boolean>;
  requestActivityOutputChanges: (
    activityId: string,
    outputId: string,
    version: number,
    reviewerId: string,
    requestedAt: string,
  ) => Promise<boolean>;
  reviewActivityOutput: (
    activityId: string,
    outputId: string,
    version: number,
    reviewerId: string,
    reviewedAt: string,
  ) => Promise<boolean>;
  currentStateMappingStatus: CurrentStateMappingStartStatus;
  currentStateMappingError: string | null;
  pendingCurrentStateMapping: CurrentStateMappingSetupInput | null;
  startCurrentStateMapping: (
    setup: CurrentStateMappingSetupInput,
  ) => Promise<CurrentStateMappingStartResult | null>;
}

type DiscoveryPlanResponse = {
  plan: DiscoveryPlan;
  revision: string;
};

type PendingCommand = {
  decisionId: string;
  outcome: DiscoveryDecisionOutcome;
};

type PendingCurrentStateMapping = {
  commandId: string;
  setup: CurrentStateMappingSetupInput;
};

function discoveryPlanUrl(engagementId: string): string {
  return `/api/engagements/${encodeURIComponent(engagementId)}/discoveryPlan`;
}

function initiationStorageKey(engagementId: string): string {
  return `arcpilot.current-state-mapping.${engagementId}`;
}

function readPendingInitiation(engagementId: string | null): PendingCurrentStateMapping | null {
  if (!engagementId) return null;
  try {
    const value = window.sessionStorage.getItem(initiationStorageKey(engagementId));
    return value ? JSON.parse(value) as PendingCurrentStateMapping : null;
  } catch {
    return null;
  }
}

function writePendingInitiation(engagementId: string, pending: PendingCurrentStateMapping | null): void {
  try {
    if (pending) window.sessionStorage.setItem(initiationStorageKey(engagementId), JSON.stringify(pending));
    else window.sessionStorage.removeItem(initiationStorageKey(engagementId));
  } catch {
    // Session storage is a recovery aid only; the durable receipt remains server-owned.
  }
}

function commandId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `mapping-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useDiscoveryPlan(standalonePlan?: DiscoveryPlan): UseDiscoveryPlanReturn {
  const {
    currentEngagementId,
    loading: engagementLoading,
    refreshEngagement,
  } = useEngagement();
  const standalonePlanRef = useRef(standalonePlan ?? null);
  const [plan, setPlan] = useState<DiscoveryPlan | null>(standalonePlanRef.current);
  const [revision, setRevision] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingDecisionId, setPendingDecisionId] = useState<string | null>(null);
  const [error, setError] = useState<DiscoveryPlanError | null>(null);
  const [pendingActivityCommand, setPendingActivityCommand] = useState<ActivityOutputCommandError['command'] | null>(null);
  const [activityOutputError, setActivityOutputError] = useState<ActivityOutputCommandError | null>(null);
  const [currentStateMappingStatus, setCurrentStateMappingStatus] = useState<CurrentStateMappingStartStatus>('idle');
  const [currentStateMappingError, setCurrentStateMappingError] = useState<string | null>(null);
  const [pendingCurrentStateMapping, setPendingCurrentStateMapping] = useState<CurrentStateMappingSetupInput | null>(null);
  const lastCommandRef = useRef<PendingCommand | null>(null);
  const requestSequenceRef = useRef(0);

  const loadLatestPlan = useCallback(async (): Promise<void> => {
    const engagementId = currentEngagementId;
    const requestSequence = ++requestSequenceRef.current;
    setError(null);
    setActivityOutputError(null);
    setPendingDecisionId(null);

    if (!engagementId) {
      setPlan(standalonePlanRef.current);
      setRevision(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(discoveryPlanUrl(engagementId));
      if (!response.ok) throw new Error('load failed');
      const body = (await response.json()) as DiscoveryPlanResponse;
      if (requestSequence !== requestSequenceRef.current) return;
      setPlan(body.plan);
      setRevision(body.revision);
    } catch {
      if (requestSequence !== requestSequenceRef.current) return;
      setPlan(null);
      setRevision(null);
      setError({ decisionId: null, message: 'Discovery plan could not be loaded.' });
    } finally {
      if (requestSequence === requestSequenceRef.current) setLoading(false);
    }
  }, [currentEngagementId]);

  useEffect(() => {
    if (engagementLoading) {
      setLoading(true);
      return;
    }
    void loadLatestPlan();
  }, [engagementLoading, loadLatestPlan]);

  useEffect(() => {
    const pending = readPendingInitiation(currentEngagementId);
    setPendingCurrentStateMapping(pending?.setup ?? null);
    setCurrentStateMappingStatus(pending ? 'recovery' : 'idle');
    setCurrentStateMappingError(null);
  }, [currentEngagementId]);

  const resolveDecision = useCallback(async (
    decisionId: string,
    outcome: DiscoveryDecisionOutcome,
  ): Promise<void> => {
    lastCommandRef.current = { decisionId, outcome };
    setError(null);
    setPendingDecisionId(decisionId);

    if (!currentEngagementId && standalonePlanRef.current) {
      setPlan((currentPlan) => {
        if (!currentPlan) return currentPlan;
        return {
          ...currentPlan,
          scopeDecisions: currentPlan.scopeDecisions.map((decision) => (
            decision.id === decisionId
              ? { ...decision, state: outcome === 'accept' ? 'accepted' : 'rejected' }
              : decision
          )),
          activities: outcome === 'accept'
            ? currentPlan.activities.map((activity) => (
              activity.sourceDecisionId === decisionId
                ? { ...activity, state: 'planned' as const }
                : activity
            ))
            : currentPlan.activities.filter((activity) => activity.sourceDecisionId !== decisionId),
        };
      });
      lastCommandRef.current = null;
      setPendingDecisionId(null);
      return;
    }

    if (!currentEngagementId || !plan) {
      setPendingDecisionId(null);
      setError({ decisionId, message: 'Discovery plan could not be saved.' });
      return;
    }

    try {
      const response = await fetch(`${discoveryPlanUrl(currentEngagementId)}/scope/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decisionId, outcome, expectedRevision: revision }),
      });

      if (response.status === 409) {
        const conflict = (await response.json()) as { code?: string };
        if (conflict.code === 'DISCOVERY_PLAN_CONFLICT') {
          setError({
            decisionId,
            message: 'This Discovery plan has changed since you opened it.',
            conflict: true,
          });
          return;
        }
      }
      if (!response.ok) throw new Error('save failed');

      const body = (await response.json()) as DiscoveryPlanResponse;
      setPlan(body.plan);
      setRevision(body.revision);
      lastCommandRef.current = null;
    } catch {
      setError({ decisionId, message: 'Discovery plan could not be saved.' });
    } finally {
      setPendingDecisionId(null);
    }
  }, [currentEngagementId, plan, revision]);

  const retryDecision = useCallback(async (decisionId: string): Promise<void> => {
    const command = lastCommandRef.current;
    if (!command || command.decisionId !== decisionId) return;
    await resolveDecision(command.decisionId, command.outcome);
  }, [resolveDecision]);

  const runActivityCommand = useCallback(async (
    command: ActivityOutputCommandError['command'],
    path: string,
    body: Record<string, unknown>,
  ): Promise<boolean> => {
    setActivityOutputError(null);
    setPendingActivityCommand(command);

    if (!currentEngagementId || !plan) {
      setPendingActivityCommand(null);
      setActivityOutputError({ command, message: 'The activity could not be updated.' });
      return false;
    }

    try {
      const response = await fetch(`${discoveryPlanUrl(currentEngagementId)}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, expectedRevision: revision }),
      });

      if (response.status === 409) {
        const conflict = (await response.json()) as { code?: string };
        if (conflict.code === 'ACTIVITY_OUTPUT_CONFLICT' || conflict.code === 'DISCOVERY_PLAN_CONFLICT') {
          setActivityOutputError({
            command,
            message: 'This activity output has changed since you opened it.',
            conflict: true,
          });
          return false;
        }
      }
      if (!response.ok) throw new Error('activity command failed');

      const result = (await response.json()) as DiscoveryPlanResponse;
      setPlan(result.plan);
      setRevision(result.revision);
      return true;
    } catch {
      setActivityOutputError({ command, message: 'The activity could not be updated. Try again.' });
      return false;
    } finally {
      setPendingActivityCommand(null);
    }
  }, [currentEngagementId, plan, revision]);

  const linkActivitySession = useCallback((activityId: string, sessionId: string) => (
    runActivityCommand(
      'link',
      `activities/${encodeURIComponent(activityId)}/sessions/link`,
      { sessionId },
    )
  ), [runActivityCommand]);

  const returnActivityOutput = useCallback((
    activityId: string,
    input: ReturnActivityOutputInput,
  ) => (
    runActivityCommand(
      'return',
      `activities/${encodeURIComponent(activityId)}/outputs/return`,
      input as unknown as Record<string, unknown>,
    )
  ), [runActivityCommand]);

  const requestActivityOutputChanges = useCallback((
    activityId: string,
    outputId: string,
    version: number,
    reviewerId: string,
    requestedAt: string,
  ) => (
    runActivityCommand(
      'changes',
      `activities/${encodeURIComponent(activityId)}/outputs/${encodeURIComponent(outputId)}/versions/${version}/changes`,
      { reviewerId, requestedAt },
    )
  ), [runActivityCommand]);

  const reviewActivityOutput = useCallback((
    activityId: string,
    outputId: string,
    version: number,
    reviewerId: string,
    reviewedAt: string,
  ) => (
    runActivityCommand(
      'review',
      `activities/${encodeURIComponent(activityId)}/outputs/${encodeURIComponent(outputId)}/versions/${version}/review`,
      { reviewerId, reviewedAt },
    )
  ), [runActivityCommand]);

  const startCurrentStateMapping = useCallback(async (
    setup: CurrentStateMappingSetupInput,
  ): Promise<CurrentStateMappingStartResult | null> => {
    if (!currentEngagementId || !plan) {
      setCurrentStateMappingStatus('failed');
      setCurrentStateMappingError('The current Discovery plan is unavailable.');
      return null;
    }
    const stored = readPendingInitiation(currentEngagementId);
    const pending = stored && JSON.stringify(stored.setup) === JSON.stringify(setup)
      ? stored
      : { commandId: commandId(), setup };
    writePendingInitiation(currentEngagementId, pending);
    setPendingCurrentStateMapping(setup);
    setCurrentStateMappingError(null);

    const body = {
      commandId: pending.commandId,
      expectedRevision: revision,
      question: setup.question,
      streamName: setup.streamName,
      startBoundary: setup.startBoundary,
      endBoundary: setup.endBoundary,
      participants: setup.participants.split(',').map((name) => name.trim()).filter(Boolean),
      reviewer: setup.reviewer,
    };

    for (let attempt = 0; attempt < 2; attempt += 1) {
      setCurrentStateMappingStatus(attempt === 0 && !stored ? 'starting' : 'resuming');
      try {
        const response = await fetch(`${discoveryPlanUrl(currentEngagementId)}/current-state-mapping/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const failure = await response.json().catch(() => ({})) as { error?: string; code?: string };
          if (response.status === 409) {
            await loadLatestPlan();
            setCurrentStateMappingStatus('recovery');
            setCurrentStateMappingError(failure.error || 'The Discovery plan changed while mapping was starting.');
            return null;
          }
          setCurrentStateMappingStatus('failed');
          setCurrentStateMappingError(failure.error || 'Mapping could not be started.');
          return null;
        }
        const result = await response.json() as CurrentStateMappingStartResult;
        writePendingInitiation(currentEngagementId, null);
        setPendingCurrentStateMapping(null);
        setCurrentStateMappingStatus('idle');
        await Promise.all([loadLatestPlan(), refreshEngagement()]);
        return result;
      } catch {
        if (attempt === 0) continue;
      }
    }

    setCurrentStateMappingStatus('recovery');
    setCurrentStateMappingError('ArcPilot could not confirm that setup finished. Your details are safe.');
    return null;
  }, [currentEngagementId, loadLatestPlan, plan, refreshEngagement, revision]);

  return {
    plan,
    revision,
    loading,
    pendingDecisionId,
    error,
    resolveDecision,
    retryDecision,
    loadLatestPlan,
    pendingActivityCommand,
    activityOutputError,
    linkActivitySession,
    returnActivityOutput,
    requestActivityOutputChanges,
    reviewActivityOutput,
    currentStateMappingStatus,
    currentStateMappingError,
    pendingCurrentStateMapping,
    startCurrentStateMapping,
  };
}
